import { useCallback, useEffect, useRef, useState } from 'react';
import { AddButtonIcon } from '../../icons';
import EventModal from '../../modals/EventModal';
import { JWCalendar, JWEventsList, JWCalendarWithEvents } from '../../Calendar';
import { EventType, Value } from '../../../types/calendar';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import axiosService from '../../../utils/axios';
import calendarSlice from '../../../store/slices/calendar';
import { ProfileInfoType } from '../../../types/profile';
import { PROFILE_MODULE_CLASS } from '@/theme/surfaceClasses';

interface EventSectionProps {
    canEdit: boolean
    profileInfo: ProfileInfoType
}

const EventSection = ({ canEdit, profileInfo }: EventSectionProps) => {
    const [showAddEventModal, setShowAddEventModal] = useState(false);
    const [eventCount, setEventCount] = useState<number | null>(null);
    const [calendarValue, setCalendarValue] = useState<Value>(new Date());
    const eventsList = useSelector((state: RootState) => {
        return state.calendar.eventsList;
    });
    const [selectedEvent, setSelectedEvent] = useState<EventType | undefined>(undefined);
    const dispatch = useDispatch();
    const profileId = profileInfo?._id;
    const fetchGenerationRef = useRef(0);

    const createEvent = () => {
        setSelectedEvent({
            _id: '',
            title: '',
            dateTime: calendarValue as Date,
            description: '',
            link: '',
            flyer_url: '',
            ticket_link: '',
        });
        setShowAddEventModal(true);
    };

    const openEventModal = (event: EventType) => {
        setSelectedEvent(event);
        setShowAddEventModal(true);
    };

    const closeEventModal = () => {
        setShowAddEventModal(false);
    };

    useEffect(() => {
        if (selectedEvent) {
            setCalendarValue(new Date(selectedEvent.dateTime));
            return;
        }
    }, [selectedEvent]);

    const fetchEvents = useCallback(async () => {
        if (!profileId) {
            return;
        }

        const generation = ++fetchGenerationRef.current;

        try {
            const { data } = await axiosService.get<EventType[]>(
                `/events?profileId=${profileId}`
            );

            if (generation !== fetchGenerationRef.current) {
                return;
            }

            dispatch(calendarSlice.actions.setEventsList(data));
            setEventCount(data.length);

            const now = new Date();
            now.setHours(0, 0, 0, 0); // Set to start of day to include today's events

            // Sort events by date first
            const sortedEvents = [...data].sort((a, b) =>
                new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
            );

            // Next upcoming event, or the most recent past event when nothing is ahead.
            const upcomingEvent = sortedEvents.find(event => new Date(event.dateTime) >= now);
            const focusEvent = upcomingEvent || sortedEvents[sortedEvents.length - 1];

            if (focusEvent) {
                const eventDate = new Date(focusEvent.dateTime);
                setSelectedEvent(focusEvent);
                setCalendarValue(eventDate);
                dispatch(calendarSlice.actions.setIsoDate(eventDate.toISOString()));
            } else {
                setSelectedEvent(undefined);
                setCalendarValue(now);
                dispatch(calendarSlice.actions.setIsoDate(now.toISOString()));
            }
        } catch (error) {
            if (generation !== fetchGenerationRef.current) {
                return;
            }
            console.error('Error fetching events:', error);
            setEventCount(0);
        }
    }, [dispatch, profileId]);

    useEffect(() => {
        setSelectedEvent(undefined);
        setEventCount(null);
        dispatch(calendarSlice.actions.setEventsList([]));
        fetchEvents();
    }, [dispatch, fetchEvents, profileId]);

    if (eventCount === null || eventCount === 0) {
        if (!canEdit || eventCount === null) return null;
        return (
            <div className={`${PROFILE_MODULE_CLASS} p-4`}>
                <div className="flex flex-row justify-between items-center mb-2">
                    <h2 className="text-lg font-bold text-eerieblack">Events</h2>
                    <button
                        className="bg-white p-[6px] rounded-full border-gray-400 border-[1px] cursor-pointer"
                        onClick={createEvent}
                        aria-label="Add an event"
                    >
                        <AddButtonIcon width={24} height={24} fill={'#626262'} fillOpacity={0.8} />
                    </button>
                </div>
                <p className="text-sm text-gray-500">Playing a show or hosting a session? Add it so people can find it.</p>
                <EventModal
                    event={selectedEvent}
                    show={showAddEventModal}
                    setShow={setShowAddEventModal}
                    onClose={closeEventModal}
                    fetchEvents={fetchEvents}
                    setSelectedEvent={setSelectedEvent}
                />
            </div>
        );
    }

    return (
        <div className={`${PROFILE_MODULE_CLASS} p-4`}>
            <div className='flex flex-row justify-end mb-2'>
                {canEdit && (
                    <button
                        className="bg-white p-[6px] rounded-full border-gray-400 border-[1px] cursor-pointer"
                        onClick={createEvent}
                    >
                        <AddButtonIcon
                            width={24}
                            height={24}
                            fill={'#626262'}
                            fillOpacity={0.8}
                        />
                    </button>
                )}
            </div>
            <EventModal
                event={selectedEvent}
                show={showAddEventModal}
                setShow={setShowAddEventModal}
                onClose={closeEventModal}
                fetchEvents={fetchEvents}
                setSelectedEvent={setSelectedEvent}
            />
            <JWCalendarWithEvents
                calendarValue={calendarValue}
                setCalendarValue={setCalendarValue}
                eventsList={eventsList}
                createEvent={createEvent}
                openEventModal={openEventModal}
                fetchEvents={fetchEvents}
                canEdit={canEdit}
            />
        </div>
    );
};

export default EventSection;
